/**
 * A tab per objective, and what one is when there is only one objective. F-151 step 8.
 *
 * Leo, 2026-09-18: *"If objective is a tab, each tab shows the objective's progress, plan and
 * research, and each objective gets its own chat."*
 *
 * **A tab contains the unified view rather than sitting inside it**, which is why F-141 merged
 * research and plan into one screen first. The three screens a plan owns — its conversation,
 * its progress and the plan itself — are the same three for every plan, so the tab bar rides
 * above them and the section menu stays what it was. Switching plan keeps the screen; switching
 * screen keeps the plan.
 *
 * **The plan on screen is `?o=` and nothing else**, the parameter step 6 minted for a tapped
 * notification. One address per plan per screen means a tab is linkable, shareable and
 * back-buttonable, and it means the screens already know how to read it: `Chat.tsx` has read
 * `?o=` since step 6, and this step teaches the other two.
 *
 * React-free on purpose, like `nav.ts` and `plan/view.ts`: `npm test` does not type-check
 * `.tsx` at all (`tsconfig.test.json` compiles a handful of React-free modules and nothing
 * else), so a rule written inline in a component is a rule no test in this repository can see.
 * Everything below is a rule that fails silently if it is wrong — a bar that appears when it
 * should not, a tab that selects nothing, an icon that adds up the wrong numbers.
 */

/* `.js`, like `nav.ts` imports `./routes.js`: this module is compiled for plain Node as
   well as for the bundler, and Node's ESM resolver does not guess extensions. */
import { ROUTES } from './routes.js';

/** The little a tab needs to know about a plan. `Objective` in `api.ts` is a superset. */
export interface TabObjective {
  objectiveId: string;
  /** The generated name, per locale, as the API stores it. Absent for the first minute of a plan's life. */
  title?: Record<string, string>;
  /** What the learner typed. The label falls back to it — see `tabLabel`. */
  description?: string;
  /** When it was made. Used only to order the tabs — see `objectiveTabs`. */
  createdAt?: string;
  /**
   * How many messages are waiting in that plan's chat, when the API said.
   *
   * **Absent is not nought.** Absent means the count could not be taken (see
   * `unreadByObjective` in the API) or that this deploy does not answer with one; nought is a
   * claim that nothing is waiting. A tab draws a pill for the second and nothing for the first.
   */
  unread?: number;
}

/** One tab, ready to render. */
export interface ObjectiveTab {
  objectiveId: string;
  /** What the tab says. Localised by the caller — see `labelOf`. */
  label: string;
  /** Where tapping it goes: this screen, that plan. */
  to: string;
  selected: boolean;
  /** Passed through from `TabObjective`, absent for the reason given there. */
  unread?: number;
}

/**
 * The tabs to render above a screen, or none at all.
 *
 * **Fewer than two plans renders nothing, and that is a decision rather than an optimisation.**
 * Every learner has exactly one live plan today — everybody is on tier 1 or the internal 99,
 * and both grant one slot; the two and three that tiers 2 and 3 grant are priced and not sold
 * — so a bar that drew one tab would put a permanent, untappable control above every screen in
 * the app, naming the only thing it could name. That is clutter, not navigation. The bar
 * appears the moment a second plan does, which is the moment it means something.
 *
 * **Ordered by `createdAt`, oldest first, and this is the load-bearing choice in the
 * function.** The obvious order is the API's, which is newest-first for the objectives list and
 * "most recently asked from" for the default — both of which *move while the learner is looking
 * at them*: the sweep asks a question, `lastAskedAt` changes, and the tab somebody was reaching
 * for is somewhere else. Tabs are muscle memory. Creation order never changes, so a plan keeps
 * its place for the whole of its life. `objectiveId` breaks a tie, so two plans made in the same
 * millisecond still have a total order rather than an arbitrary one.
 *
 * **The named plan is marked, and when the address names a plan that is not live, nothing is.**
 * That case is ordinary — a `?o=` outlives the plan it names, in an old notification or a plan
 * closed on another device — and the first draft of this marked the first tab instead. A
 * review caught what that means: the screens do not agree about what to render for a dead
 * `?o=` (the chat reads that thread, the plan screen falls back to the default), so marking a
 * tab the learner is not looking at is the bar telling them they are somewhere they are not.
 * No mark is the truthful statement — *what is on screen is not one of these* — and every tab
 * is one tap away from fixing it.
 */
export function objectiveTabs(input: {
  /** Every live plan, as `GET /api/objectives/current` answers. `undefined` until it has. */
  objectives: readonly TabObjective[] | undefined;
  /** The plan on screen: `?o=`, or the plan the API defaulted to. */
  selected: string | undefined;
  /** The screen the tabs stay on — a value of `ROUTES`, so switching plan keeps the section. */
  path: string;
  /** What to call a plan. Localisation and truncation live in the component. */
  labelOf: (objective: TabObjective, index: number) => string;
}): ObjectiveTab[] {
  const objectives = input.objectives ?? [];
  if (objectives.length < 2) return [];

  const ordered = [...objectives].sort((a, b) => {
    const made = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    return made !== 0 ? made : a.objectiveId.localeCompare(b.objectiveId);
  });

  return ordered.map((objective, index) => ({
    objectiveId: objective.objectiveId,
    label: input.labelOf(objective, index),
    to: planPath(input.path, objective.objectiveId),
    selected: objective.objectiveId === input.selected,
    ...(objective.unread === undefined ? {} : { unread: objective.unread }),
  }));
}

/**
 * The longest a tab's own words may run before they are cut.
 *
 * Twenty-four characters is about three words, which is what a title generated for an
 * objective actually is, and it is what fits on a phone beside a second tab without either
 * of them becoming an ellipsis. Longer labels are cut here rather than by CSS because a
 * label cut by `text-overflow` is still the whole string to a screen reader and to the
 * `title` attribute, and the point of the cut is the width.
 */
const MAX_LABEL = 24;

/**
 * What a tab calls a plan.
 *
 * **The title, then the learner's own words, then a number.** A title is three or so words
 * written for exactly this purpose and is nearly always there. A plan that has just been
 * described has no title yet — it is generated when the objective is created — and the first
 * few words of what the learner typed are a far better label than "Plan 2" for the minute or
 * two before the real one lands. The numbered fallback exists so that two untitled plans are
 * never two identical tabs, which is a bar you cannot navigate with.
 *
 * The title arrives already localised: `localised()` needs the reader's locale and this module
 * stays React-free so that it can be tested, so the component resolves it and passes a string.
 */
export function tabLabel(input: {
  /** The plan's generated name, resolved for the reader. */
  title: string | undefined;
  /** What the learner typed, kept verbatim. */
  description: string | undefined;
  /** A translated word for a plan, which becomes "<word> 2" when there is nothing else. */
  fallback: string;
  /** Where this plan sits in the bar, counted from nought. */
  index: number;
}): string {
  const title = (input.title ?? '').trim();
  if (title !== '') return cut(title);
  const description = (input.description ?? '').trim();
  if (description !== '') return cut(description);
  return `${input.fallback} ${input.index + 1}`;
}

/**
 * The cut itself: whole words where it can, and an ellipsis so the cut is visible.
 *
 * A label cut mid-word reads as a typo rather than as a truncation, and the last space is
 * cheap to find. A single word longer than the limit is cut anyway — there is nothing else to
 * do with it, and it is still the beginning of the right word.
 */
function cut(text: string): string {
  if (text.length <= MAX_LABEL) return text;
  const clipped = text.slice(0, MAX_LABEL);
  const space = clipped.lastIndexOf(' ');
  return `${(space > MAX_LABEL / 2 ? clipped.slice(0, space) : clipped).trimEnd()}…`;
}

/**
 * One screen, one plan, as an address.
 *
 * Its own function because three screens and the tabs all build it, and a query string
 * assembled by hand in four places is three chances to forget the encoding. `?o=` rather than a
 * path segment because that is what `sw.js` already puts in a notification and what `App.tsx`
 * already carries through a redirect — a second spelling of "which plan" would need both taught
 * about it.
 */
export function planPath(path: string, objectiveId: string): string {
  return `${path}?o=${encodeURIComponent(objectiveId)}`;
}

/**
 * The three screens that are about one plan, which is what the tab bar sits above.
 *
 * Configuration and the profile are not here and must not be: messaging settings are global
 * by Leo's decision — *"Messaging settings is global, I define globally, NOT per objective"* —
 * and an address that carried a plan into them would suggest the controls on them act on it.
 *
 * Paths from `ROUTES`, so a rename is a compile error rather than a list that quietly stops
 * matching. The same argument `nav.ts` makes about its own table, and for the same reason.
 */
export const PLAN_SCREENS: readonly string[] = [ROUTES.chat, ROUTES.progress, ROUTES.plan];

/**
 * Where a menu entry goes, given the plan the learner is looking at.
 *
 * **Switching plan keeps the screen and switching screen keeps the plan.** Without this the
 * second half is not true: the menu links to bare paths, so a learner reading plan B's plan who
 * tapped *Progress* would get plan A's numbers — the default plan, under no tab they chose, with
 * nothing on the screen to say what had happened. That is the cross-plan confusion every other
 * part of this step is built to avoid, arriving through the menu.
 *
 * The plan is dropped for every other destination rather than carried and ignored, because an
 * address that names a plan is a claim that the screen is about it.
 */
export function sectionHref(path: string, objectiveId: string | undefined): string {
  if (!objectiveId || !PLAN_SCREENS.includes(path)) return path;
  return planPath(path, objectiveId);
}

/**
 * The number for the app icon, across every plan.
 *
 * **The icon is one icon.** The Badging API puts a single number on a single home-screen icon,
 * so per-plan counts belong on the tabs and the icon owes the learner a total — which is the
 * whole reason step 8 rewrote the marker: over a plan-major thread, one `lastSeenMessageId`
 * could only ever be right about one plan, so the total it produced was not a total. Each plan
 * now counts against its own marker and the icon adds them up.
 *
 * **The thread on screen overrides its own entry**, because it is fresher: the chat re-reads
 * its thread every twenty seconds and the plans list is read when a screen opens. Without the
 * override the icon would still show a question the learner has just read, for as long as they
 * stayed on the screen — the exact behaviour F-120 exists to prevent.
 *
 * **`undefined` unless every plan has a number**, and the caller must then leave the badge
 * alone rather than clear it. A total missing one plan is not a smaller total, it is a wrong
 * one — and it is wrong in the direction that hides a waiting question, which is the failure
 * F-120 exists to prevent. It is also the rule `dispatch.ts` applies to the same event on the
 * push path ("a plan whose count throws takes the whole badge with it"), and two policies
 * about one icon is how it comes to flicker between two numbers. A count that failed and a
 * count of nought are opposite messages.
 *
 * The thread's own plan is exempt from that requirement, because the thread *is* its number.
 * That is also what keeps this working against an API deployed before step 8: it answers no
 * counts at all, the learner has one plan, and that plan is the thread on screen.
 *
 * Nothing is capped here. Each number is already the API's bounded answer — fifty per plan,
 * past which an icon means "a lot" — and a third ceiling in a third package is how three
 * numbers come to disagree about the same threads.
 */
export function iconCount(input: {
  objectives: readonly TabObjective[] | undefined;
  /** The thread the chat is showing, when it is showing one. */
  thread?: { objectiveId: string | undefined; unread: number | undefined };
}): number | undefined {
  /* The thread's contribution, as a pair or not at all — narrowed once here so that neither
     half can be read without the other below. */
  const thread = input.thread;
  const fromThread: { objectiveId: string; unread: number } | undefined =
    thread && thread.objectiveId !== undefined && typeof thread.unread === 'number'
      ? { objectiveId: thread.objectiveId, unread: thread.unread }
      : undefined;

  const counts = new Map<string, number>();
  for (const objective of input.objectives ?? []) {
    if (typeof objective.unread === 'number') {
      counts.set(objective.objectiveId, objective.unread);
    } else if (objective.objectiveId !== fromThread?.objectiveId) {
      // One plan nobody could count makes the total a claim nobody can stand behind. See above.
      return undefined;
    }
  }
  if (fromThread) counts.set(fromThread.objectiveId, fromThread.unread);
  if (counts.size === 0) return undefined;

  let total = 0;
  for (const count of counts.values()) total += Math.max(0, count);
  return total;
}
